import React, { useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import {
    Box,
    Collapse,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Stack,
    useTheme,
} from '@mui/material';
import { ExpandMore, ExpandLess, InfoOutlined, EditOutlined } from '@mui/icons-material';
import { useEntityData } from 'c-data';
import type { Campaign } from 'c-sdk';
import { CampaignStatus, PermissionName } from 'c-sdk';
import { useUserPermissions } from 'c-auth-module/Hooks';
import { ConditionalDebugWrapper } from 'c-auth-module/Components';
import { useAPIClientRequest, useIsMobileView } from 'c-hooks';
import { useBoolean } from 'react-hanger';
import to from 'await-to-js';
import apiClient from 'c-data/apiClient';
import { useAtom } from 'jotai';

import useCampaignWrapperState from 'c-main/Components/Campaign/CampaignReports/useCampaignWrapperState';
import CampaignMetaSection from 'c-main/Pages/Campaign/CampaignOverviewPage/SideBar/CampaignMetaSection';
import PCACustomizationDialog from 'c-main/Components/Campaign/CampaignOverviewDetailTabs/PCACustomizationDialog';
import SidebarControlsList from './ControlsList';
import CampaignHeader from './CampaignHeader';
import DialogsContainer from './DialogsContainer';
import { reportingInputDefaults } from './reportingInputs';
import TabsManager from './TabsManager';
import { CampaignUpdateDialogPermissions, UpdateDisplaysPermissions } from './constants';
import { atom_summaryCustomizationDialogOpen } from '../atoms';

interface SidebarProps {
    id: number;
    mobileDrawerOpen?: boolean;
    toggleMobileDrawer?: () => void;
}

const Sidebar = ({ id, mobileDrawerOpen, toggleMobileDrawer }: SidebarProps) => {
    const theme = useTheme();
    const isMobileView = useIsMobileView();
    const [localMobileDrawerOpen, setLocalMobileDrawerOpen] = useState(false);
    const { source } = useCampaignWrapperState(id);

    const reportingOpen = useBoolean(false);
    const digitalOpen = useBoolean(false);
    const classicOpen = useBoolean(false);
    const campaignInfoOpen = useBoolean(true);
    const groupActions = useBoolean(false);

    const toggleLocalMobileDrawer = useCallback(() => {
        setLocalMobileDrawerOpen(prev => !prev);
    }, []);

    const isDrawerOpen = mobileDrawerOpen !== undefined ? mobileDrawerOpen : localMobileDrawerOpen;
    const handleToggleDrawer = toggleMobileDrawer || toggleLocalMobileDrawer;

    const { getById, upsertEntity } = useEntityData<Campaign>('Campaign');
    const campaign = getById({ id });

    const { start, error, isLoading } = useAPIClientRequest(apiClient.Entities.Campaign.update);
    const { start: listStart, data: listData } = useAPIClientRequest(
        apiClient.Entities.Buyer.happyDemics,
    );
    const {
        start: StartArchive,
        isLoading: IsArchiving,
        hasFailed: HasFailedArchiving,
        error: ArchivingError,
    } = useAPIClientRequest(apiClient.Entities.Campaign.archive);

    const { hasAll } = useUserPermissions();

    // The edit button shows for either permission set, so list both.
    const editCampaignDebugLines = useMemo(
        () => [
            `All of: ${CampaignUpdateDialogPermissions.join(', ')}`,
            `Or all of: ${UpdateDisplaysPermissions.join(', ')}`,
        ],
        [],
    );

    const isCampaignArchived = useMemo(
        () => campaign?.status === CampaignStatus.CampaignArchived,
        [campaign?.status],
    );

    useEffect(() => {
        if (hasAll([PermissionName.UniledPortalHappyDemicsRead])) {
            listStart();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [searchTerm, setSearchTerm] = useState('');
    const currentHappydemicsId = useMemo(() => campaign.happydemics_id ?? null, [campaign]);

    const editCampaignDialogState = useBoolean(false);
    const campaignShareDialogState = useBoolean(false);
    const campaignBookingSummaryState = useBoolean(false);
    const pcaState = useBoolean(false);
    const pptPcaState = useBoolean(false);
    const iOIdsState = useBoolean(false);
    const happyDemicsState = useBoolean(false);
    const reportingInputsState = useBoolean(false);
    const pcaCustomizationState = useBoolean(false);
    const creativeScheduleCheckState = useBoolean(false);
    const [, setSummaryCustomizationDialogOpen] = useAtom(atom_summaryCustomizationDialogOpen);

    const [happyDemicsChoice, setHappyDemicsChoice] = useState<number | null>(
        campaign.happydemics_id ?? null,
    );

    const performArchive = useCallback(async () => {
        if (IsArchiving) return false;
        const [err, response] = await to(StartArchive(id, !isCampaignArchived));
        if (!err && response?.data?.data?.id === id) {
            upsertEntity(response.data.data as unknown as Campaign);
            return true;
        }
        return false;
    }, [IsArchiving, StartArchive, id, isCampaignArchived, upsertEntity]);

    const [insertionOrderIds, setInsertionOrderIds] = useState<string>(
        campaign?.dsp_insertion_order_ids ? campaign?.dsp_insertion_order_ids.join('\n') : '',
    );

    const removeHappyDemicsFromCampaign = useCallback(async () => {
        setHappyDemicsChoice(null);
        const [err, response] = await to(start(id, { happydemics_id: null }));
        if (!err && response?.data?.data?.id === id) {
            upsertEntity(response.data.data as unknown as Campaign);
            happyDemicsState.setFalse();
        }
    }, [happyDemicsState, id, start, upsertEntity]);

    const updateDspInsertionOrderIds = useCallback(async () => {
        const dsp_insertion_order_ids = Array.from(
            new Set(
                insertionOrderIds
                    .split('\n')
                    .map(value => value.trim())
                    .filter(v => v !== ''),
            ),
        );
        const [err, response] = await to(start(id, { dsp_insertion_order_ids }));
        if (!err && response?.data?.data?.id === id) {
            upsertEntity(response.data.data as unknown as Campaign);
            setInsertionOrderIds(dsp_insertion_order_ids.join('\n'));
            iOIdsState.setFalse();
        }
    }, [iOIdsState, id, insertionOrderIds, start, upsertEntity]);

    const filteredListData = useMemo(
        () =>
            listData?.data?.data.filter(
                item =>
                    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    item.id.toString().includes(searchTerm),
            ) || [],
        [listData, searchTerm],
    );

    const checkboxHandler = useCallback((e: React.ChangeEvent<HTMLInputElement>, id: number) => {
        setHappyDemicsChoice(prev => (prev === id ? null : id));
    }, []);

    const addHappyDemicsToCampaign = useCallback(async () => {
        const [err, response] = await to(
            start(id, { happydemics_id: happyDemicsChoice }, ['happydemics']),
        );
        if (!err && response?.data?.data?.id === id) {
            upsertEntity(response.data.data as unknown as Campaign);
            happyDemicsState.setFalse();
        }
    }, [happyDemicsChoice, happyDemicsState, id, start, upsertEntity]);

    // UL-8133 -- `vistar_us2` derives Expected Impressions from the IO targets; the API refuses it.
    const defaultData = useMemo(() => reportingInputDefaults(campaign), [campaign]);

    const bodyRef = useRef<HTMLDivElement | null>(null);
    const metaRef = useRef<HTMLDivElement | null>(null);
    const [onlyScrollBelowMeta, setOnlyScrollBelowMeta] = useState(false);
    const [belowMaxHeight, setBelowMaxHeight] = useState<number | undefined>(undefined);

    const recomputeScrollMode = useCallback(() => {
        const body = bodyRef.current;
        const meta = metaRef.current;
        if (!body || !meta) return;

        const bodyRect = body.getBoundingClientRect();
        const metaRect = meta.getBoundingClientRect();

        const metaFullyVisible = metaRect.bottom <= bodyRect.bottom;

        if (metaFullyVisible) {
            setOnlyScrollBelowMeta(true);
            const remaining = Math.max(0, bodyRect.bottom - metaRect.bottom);
            setBelowMaxHeight(remaining);
        } else {
            setOnlyScrollBelowMeta(false);
            setBelowMaxHeight(undefined);
        }
    }, []);

    useLayoutEffect(() => {
        recomputeScrollMode();
        const ro = new ResizeObserver(recomputeScrollMode);
        if (bodyRef.current) ro.observe(bodyRef.current);
        if (metaRef.current) ro.observe(metaRef.current);
        window.addEventListener('resize', recomputeScrollMode);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', recomputeScrollMode);
        };
    }, [recomputeScrollMode]);

    return (
        <>
            <PCACustomizationDialog dialogState={pcaCustomizationState} hideTrigger />
            <DialogsContainer
                campaign={campaign}
                id={id}
                editCampaignDialogState={editCampaignDialogState}
                campaignShareDialogState={campaignShareDialogState}
                campaignBookingSummaryState={campaignBookingSummaryState}
                pcaState={pcaState}
                pptPcaState={pptPcaState}
                iOIdsState={iOIdsState}
                happyDemicsState={happyDemicsState}
                insertionOrderIds={insertionOrderIds}
                setInsertionOrderIds={setInsertionOrderIds}
                updateDspInsertionOrderIds={updateDspInsertionOrderIds}
                isLoading={isLoading}
                error={error}
                currentHappydemicsId={currentHappydemicsId}
                listData={listData}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                filteredListData={filteredListData}
                checkboxHandler={checkboxHandler}
                happyDemicsChoice={happyDemicsChoice}
                addHappyDemicsToCampaign={addHappyDemicsToCampaign}
                removeHappyDemicsFromCampaign={removeHappyDemicsFromCampaign}
                defaultData={defaultData}
                reportingInputsState={reportingInputsState}
                creativeScheduleCheckState={creativeScheduleCheckState}
                source={source}
            />

            <Drawer
                variant={isMobileView ? 'temporary' : 'permanent'}
                open={isMobileView ? isDrawerOpen : true}
                onClose={handleToggleDrawer}
                PaperProps={{
                    sx: {
                        width: isMobileView
                            ? '100%'
                            : `var(--app-sidebar-stop, ${theme.spacing(29)})`,
                        transition: isMobileView ? undefined : 'width 200ms ease',
                        boxSizing: 'border-box',
                        position: 'fixed',
                        top: isMobileView ? 0 : theme.spacing(8),
                        height: isMobileView ? '100%' : `calc(100vh - ${theme.spacing(8)})`,
                        borderRight: '1px solid #e0e0e0',
                        display: 'flex',
                        flexDirection: 'column',
                        p: 0,
                        zIndex: 1200,
                    },
                }}
                sx={{
                    zIndex: 100,
                    width: isMobileView ? '100%' : `var(--app-sidebar-stop, ${theme.spacing(29)})`,
                    flexShrink: 0,
                }}
            >
                <CampaignHeader
                    campaign={campaign}
                    isMobileView={isMobileView}
                    handleToggleDrawer={handleToggleDrawer}
                />

                <Box
                    ref={bodyRef}
                    sx={{
                        flexGrow: 1,
                        minHeight: 0,
                        overflowY: onlyScrollBelowMeta ? 'hidden' : 'auto',
                        overscrollBehavior: 'contain',
                        WebkitOverflowScrolling: 'touch',
                        pb: 1.5,
                    }}
                >
                    <Box ref={metaRef}>
                        <ListItem button onClick={campaignInfoOpen.toggle}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                gap={1}
                                sx={{ width: '100%' }}
                            >
                                <InfoOutlined color="primary" />
                                <ListItemText primary="Campaign Details" />
                                <ConditionalDebugWrapper
                                    show={
                                        hasAll(CampaignUpdateDialogPermissions) ||
                                        hasAll(UpdateDisplaysPermissions)
                                    }
                                    tooltipLines={editCampaignDebugLines}
                                >
                                    <IconButton
                                        size="small"
                                        aria-label="edit campaign"
                                        onClick={e => {
                                            e.stopPropagation();
                                            editCampaignDialogState.setTrue();
                                        }}
                                    >
                                        <EditOutlined fontSize="small" />
                                    </IconButton>
                                </ConditionalDebugWrapper>
                                {campaignInfoOpen.value ? <ExpandLess /> : <ExpandMore />}
                            </Stack>
                        </ListItem>
                        <Collapse in={campaignInfoOpen.value}>
                            <CampaignMetaSection campaign={campaign} />
                        </Collapse>
                        <Divider sx={{ width: '87%', mx: 'auto', borderColor: '#CCC' }} />
                    </Box>

                    <Box
                        sx={
                            onlyScrollBelowMeta
                                ? { maxHeight: belowMaxHeight, overflowY: 'auto' }
                                : { overflow: 'visible' }
                        }
                    >
                        <List>
                            <SidebarControlsList
                                groupActions={groupActions}
                                campaign={campaign}
                                campaignShareDialogState={campaignShareDialogState}
                                campaignBookingSummaryState={campaignBookingSummaryState}
                                pcaState={pcaState}
                                pptPcaState={pptPcaState}
                                iOIdsState={iOIdsState}
                                happyDemicsState={happyDemicsState}
                                performArchive={performArchive}
                                IsArchiving={IsArchiving}
                                HasFailedArchiving={HasFailedArchiving}
                                ArchivingError={ArchivingError}
                                reportingInputsState={reportingInputsState}
                                summaryCustomizationState={{
                                    setTrue: () => setSummaryCustomizationDialogOpen(true),
                                }}
                                pcaCustomizationState={pcaCustomizationState}
                                creativeScheduleCheckState={creativeScheduleCheckState}
                            />
                            <TabsManager
                                campaign={campaign}
                                id={id}
                                reportingOpen={reportingOpen}
                                digitalOpen={digitalOpen}
                                classicOpen={classicOpen}
                            />
                        </List>
                    </Box>
                </Box>
            </Drawer>
        </>
    );
};

export default Sidebar;
